import { addDays, compareDates } from './date';
import type {
  ItemKind,
  RescheduleProposal,
  ScheduleItem,
  TaskPart,
  TrackerDocument,
  UnavailableReason,
} from './types';

function copyDocument(document: TrackerDocument): TrackerDocument {
  return structuredClone(document);
}

function isComplete(item: ScheduleItem): boolean {
  return Object.values(item.parts).every(Boolean);
}

function finalScheduledDate(document: TrackerDocument): string {
  const activeDates = document.items
    .filter((item) => item.suggestionStatus !== 'dismissed')
    .map((item) => item.date)
    .sort(compareDates);
  return activeDates.at(-1) ?? document.startDate;
}

function incompleteParts(item: ScheduleItem): Partial<Record<TaskPart, boolean>> {
  return Object.fromEntries(
    Object.entries(item.parts).filter(([, complete]) => !complete),
  ) as Partial<Record<TaskPart, boolean>>;
}

export function togglePart(
  document: TrackerDocument,
  itemId: string,
  part: TaskPart,
  activityDate: string,
): TrackerDocument {
  const next = copyDocument(document);
  const item = next.items.find((candidate) => candidate.id === itemId);
  if (!item || !(part in item.parts)) return document;

  item.parts[part] = !item.parts[part];
  if (part === 'dpp' && !item.parts.dpp) item.accuracy = null;
  item.status = isComplete(item)
    ? 'complete'
    : Object.values(item.parts).some(Boolean)
      ? 'partial'
      : 'scheduled';
  const totalParts = Object.keys(item.parts).length;
  const completedParts = Object.values(item.parts).filter(Boolean).length;
  next.activity[activityDate] = {
    completedParts,
    totalParts,
    ended: next.activity[activityDate]?.ended ?? false,
  };
  return next;
}

export function setDppAccuracy(
  document: TrackerDocument,
  itemId: string,
  accuracy: number,
): TrackerDocument {
  const next = copyDocument(document);
  const item = next.items.find((candidate) => candidate.id === itemId);
  if (!item || !('dpp' in item.parts)) return document;
  item.accuracy = Math.max(0, Math.min(100, Math.round(accuracy)));
  return next;
}

export function previewEndDay(
  document: TrackerDocument,
  itemId: string,
  today: string,
  autoRebalance = false,
): RescheduleProposal {
  const item = document.items.find((candidate) => candidate.id === itemId);
  const oldFinishDate = finalScheduledDate(document);
  if (!item || isComplete(item)) {
    return {
      mode: 'no-change',
      currentItemId: itemId,
      oldFinishDate,
      newFinishDate: oldFinishDate,
      message: 'Everything planned for today is complete. Your schedule stays put.',
    };
  }

  const buffer = document.items
    .filter(
      (candidate) =>
        candidate.kind === 'buffer' &&
        candidate.bufferState === 'available' &&
        compareDates(candidate.date, today) > 0,
    )
    .sort((left, right) => compareDates(left.date, right.date))[0];

  if (buffer) {
    return {
      mode: 'use-buffer',
      currentItemId: itemId,
      bufferItemId: buffer.id,
      oldFinishDate,
      newFinishDate: oldFinishDate,
      carryoverDate: buffer.date,
      message: `The unfinished parts can use your ${buffer.chapter} buffer on ${buffer.date}. Your finish date will not move.`,
    };
  }

  if (autoRebalance) {
    const redistributionDate = leastLoadedDateInMonth(document, today);
    if (redistributionDate) {
      return {
        mode: 'redistribute-month',
        currentItemId: itemId,
        oldFinishDate,
        newFinishDate: oldFinishDate,
        carryoverDate: redistributionDate,
        message: `The unfinished work fits on ${redistributionDate}, your lightest remaining day this month. Your finish date stays put.`,
      };
    }
  }

  return {
    mode: 'shift-forward',
    currentItemId: itemId,
    oldFinishDate,
    newFinishDate: addDays(oldFinishDate, 1),
    carryoverDate: addDays(today, 1),
    message:
      'No buffer remains, so the unfinished parts move to tomorrow and the plan shifts by one day.',
  };
}

export function applyEndDay(
  document: TrackerDocument,
  proposal: RescheduleProposal,
  today: string,
): TrackerDocument {
  const next = copyDocument(document);
  const item = next.items.find((candidate) => candidate.id === proposal.currentItemId);
  if (!item) return document;

  next.activity[today] = {
    completedParts: Object.values(item.parts).filter(Boolean).length,
    totalParts: Object.keys(item.parts).length,
    ended: true,
  };

  if (proposal.mode === 'no-change') {
    item.status = 'complete';
    const learningSource = reconcileCarryoverCompletion(next, item);
    addLearningSuggestions(next, learningSource);
    return next;
  }

  const remaining = incompleteParts(item);
  item.status = 'partial';

  if (proposal.mode === 'use-buffer' && proposal.bufferItemId && proposal.carryoverDate) {
    const buffer = next.items.find((candidate) => candidate.id === proposal.bufferItemId);
    if (!buffer) return document;
    buffer.bufferState = 'consumed';
    buffer.status = 'skipped';
    next.items.push(createCarryover(item, remaining, proposal.carryoverDate));
  }

  if (proposal.mode === 'redistribute-month' && proposal.carryoverDate) {
    next.items.push(createCarryover(item, remaining, proposal.carryoverDate));
  }

  if (proposal.mode === 'shift-forward' && proposal.carryoverDate) {
    for (const candidate of next.items) {
      if (compareDates(candidate.date, today) > 0) candidate.date = addDays(candidate.date, 1);
    }
    next.items.push(createCarryover(item, remaining, proposal.carryoverDate));
  }

  if (proposal.mode === 'manual-date' && proposal.carryoverDate) {
    next.items.push(createCarryover(item, remaining, proposal.carryoverDate));
  }

  return next;
}

function reconcileCarryoverCompletion(
  document: TrackerDocument,
  completedItem: ScheduleItem,
): ScheduleItem {
  let learningSource = completedItem;
  let current = completedItem;
  const visited = new Set<string>();

  while (current.kind === 'carryover' && current.sourceItemId && !visited.has(current.id)) {
    visited.add(current.id);
    const source = document.items.find((candidate) => candidate.id === current.sourceItemId);
    if (!source) break;
    for (const part of Object.keys(current.parts) as TaskPart[]) {
      if (current.parts[part]) source.parts[part] = true;
    }
    if (current.accuracy !== null) source.accuracy = current.accuracy;
    source.status = isComplete(source)
      ? 'complete'
      : Object.values(source.parts).some(Boolean)
        ? 'partial'
        : 'scheduled';
    if (source.status !== 'complete') break;
    learningSource = { ...source, date: completedItem.date };
    current = source;
  }

  return learningSource;
}

function createCarryover(
  source: ScheduleItem,
  parts: Partial<Record<TaskPart, boolean>>,
  date: string,
): ScheduleItem {
  return {
    id: `${source.id}-carry-${date}`,
    date,
    chapter: source.chapter,
    topic: `${source.topic} · remaining work`,
    kind: 'carryover',
    durationMinutes: Math.max(20, Object.keys(parts).length * 25),
    parts,
    accuracy: null,
    status: 'scheduled',
    origin: 'automation',
    sourceItemId: source.id,
  };
}

export function addLearningSuggestions(document: TrackerDocument, item: ScheduleItem): void {
  if (item.accuracy !== null && item.accuracy < document.weakThreshold) {
    addSuggestion(document, item, addDays(item.date, 3), 'revision', 20, `Revisit: ${item.topic}`);
  }

  const chapterFinished =
    item.kind === 'study' &&
    document.items
      .filter((candidate) => candidate.kind === 'study' && candidate.chapter === item.chapter)
      .every((candidate) => candidate.status === 'complete');

  if (chapterFinished) {
    for (const offset of [3, 7, 21]) {
      addSuggestion(
        document,
        item,
        addDays(item.date, offset),
        'recall',
        15,
        `Recall: ${item.chapter}`,
      );
    }
  }

  if (item.kind === 'buffer' && item.bufferState === 'available') {
    for (const offset of [3, 7, 21]) {
      addSuggestion(
        document,
        item,
        addDays(item.date, offset),
        'recall',
        15,
        `Recall: ${item.chapter}`,
      );
    }
  }
}

export function addExtraWork(
  document: TrackerDocument,
  input: { topic: string; chapter?: string; minutes: number; date: string },
): TrackerDocument {
  const topic = input.topic.trim();
  if (!topic) return document;
  const next = copyDocument(document);
  const source: ScheduleItem = {
    id: crypto.randomUUID(),
    date: input.date,
    chapter: input.chapter?.trim() || document.title,
    topic,
    kind: 'practice',
    durationMinutes: Math.max(5, Math.min(600, Math.round(input.minutes))),
    parts: { review: true },
    accuracy: null,
    status: 'complete',
    origin: 'manual',
  };
  next.items.push(source);
  const activity = next.activity[input.date] ?? { completedParts: 0, totalParts: 0, ended: false };
  next.activity[input.date] = {
    ...activity,
    completedParts: activity.completedParts + 1,
    totalParts: activity.totalParts + 1,
  };
  addSuggestion(next, source, addDays(input.date, 3), 'practice', 25, `Practice: ${topic}`);
  return next;
}

export function addManualSession(
  document: TrackerDocument,
  input: {
    date: string;
    topic: string;
    chapter?: string;
    minutes: number;
    kind: Extract<ItemKind, 'study' | 'test' | 'revision' | 'recall' | 'practice'>;
  },
): TrackerDocument {
  const topic = input.topic.trim();
  if (!topic) return document;
  const next = copyDocument(document);
  const parts: Partial<Record<TaskPart, boolean>> =
    input.kind === 'study'
      ? { lecture: false, notes: false, dpp: false }
      : input.kind === 'test'
        ? { dpp: false }
        : { review: false };
  next.items.push({
    id: crypto.randomUUID(),
    date: input.date,
    chapter: input.chapter?.trim() || document.title,
    topic,
    kind: input.kind,
    durationMinutes: Math.max(5, Math.min(600, Math.round(input.minutes))),
    parts,
    accuracy: null,
    status: 'scheduled',
    origin: 'manual',
  });
  return next;
}

export interface UnavailableDayPreview {
  date: string;
  affectedSessions: number;
  moves: Array<{ itemId: string; topic: string; from: string; to: string }>;
  oldFinishDate: string;
  newFinishDate: string;
}

export function previewUnavailableDay(
  document: TrackerDocument,
  date: string,
  autoRebalance: boolean,
): UnavailableDayPreview {
  const working = copyDocument(document);
  const affected = working.items.filter(
    (item) =>
      item.date === date &&
      item.status !== 'complete' &&
      item.status !== 'skipped' &&
      item.suggestionStatus !== 'dismissed',
  );
  const oldFinishDate = finalScheduledDate(document);
  const blocked = new Set([...Object.keys(working.unavailableDays ?? {}), date]);
  const moves = affected.map((item) => {
    const nextDate = autoRebalance
      ? lightestAvailableDate(working, date, blocked)
      : nextAvailableUnblockedDate(date, blocked);
    const move = { itemId: item.id, topic: item.topic, from: date, to: nextDate };
    item.date = nextDate;
    return move;
  });
  return {
    date,
    affectedSessions: affected.length,
    moves,
    oldFinishDate,
    newFinishDate: finalScheduledDate(working),
  };
}

export function markUnavailableDay(
  document: TrackerDocument,
  input: {
    date: string;
    reason: UnavailableReason;
    note?: string;
    autoRebalance: boolean;
    createdAt?: string;
  },
): TrackerDocument {
  const preview = previewUnavailableDay(document, input.date, input.autoRebalance);
  const next = copyDocument(document);
  next.unavailableDays = {
    ...(next.unavailableDays ?? {}),
    [input.date]: {
      date: input.date,
      reason: input.reason,
      note: input.note?.trim() ?? '',
      createdAt: input.createdAt ?? new Date().toISOString(),
    },
  };
  for (const move of preview.moves) {
    const item = next.items.find((candidate) => candidate.id === move.itemId);
    if (item) item.date = move.to;
  }
  return next;
}

export function restoreAvailableDay(document: TrackerDocument, date: string): TrackerDocument {
  if (!document.unavailableDays?.[date]) return document;
  const next = copyDocument(document);
  delete next.unavailableDays?.[date];
  return next;
}

function addSuggestion(
  document: TrackerDocument,
  source: ScheduleItem,
  date: string,
  kind: 'revision' | 'recall' | 'practice',
  durationMinutes: number,
  topic: string,
): void {
  const id = `${source.id}-${kind}-${date}`;
  if (document.items.some((item) => item.id === id)) return;
  document.items.push({
    id,
    date,
    chapter: source.chapter,
    topic,
    kind,
    durationMinutes,
    parts: { review: false },
    accuracy: null,
    status: 'scheduled',
    origin: 'automation',
    suggestionStatus: 'pending',
    sourceItemId: source.id,
  });
}

export function updateSuggestion(
  document: TrackerDocument,
  itemId: string,
  status: 'accepted' | 'dismissed',
  date?: string,
): TrackerDocument {
  const next = copyDocument(document);
  const item = next.items.find(
    (candidate) => candidate.id === itemId && candidate.origin === 'automation',
  );
  if (!item) return document;
  item.suggestionStatus = status;
  if (date) item.date = date;
  return next;
}

export function moveItemAhead(
  document: TrackerDocument,
  itemId: string,
  today: string,
): TrackerDocument {
  const next = copyDocument(document);
  const item = next.items.find((candidate) => candidate.id === itemId);
  if (!item || compareDates(item.date, today) <= 0 || item.status !== 'scheduled') return document;
  const previousDate = item.date;
  item.date = today;
  item.origin = 'manual';
  for (const candidate of next.items) {
    if (
      candidate.id !== item.id &&
      candidate.origin !== 'automation' &&
      compareDates(candidate.date, previousDate) > 0
    ) {
      candidate.date = addDays(candidate.date, -1);
    }
  }
  return next;
}

export function orderedItems(document: TrackerDocument): ScheduleItem[] {
  return [...document.items]
    .filter((item) => item.suggestionStatus !== 'dismissed')
    .sort((left, right) => compareDates(left.date, right.date) || left.id.localeCompare(right.id));
}

export function projectedFinishDate(document: TrackerDocument, today: string): string {
  const historicalDays = Object.entries(document.activity).filter(([, day]) => day.ended);
  const completedParts = historicalDays.reduce((sum, [, day]) => sum + day.completedParts, 0);
  const remainingParts = document.items.reduce(
    (sum, item) => sum + Object.values(item.parts).filter((part) => !part).length,
    0,
  );
  if (completedParts === 0 || historicalDays.length === 0) return finalScheduledDate(document);
  const partsPerDay = completedParts / historicalDays.length;
  return addDays(today, Math.ceil(remainingParts / Math.max(partsPerDay, 0.5)));
}

function leastLoadedDateInMonth(document: TrackerDocument, today: string): string | null {
  const month = today.slice(0, 7);
  const candidates = [
    ...new Set(
      document.items
        .filter(
          (item) =>
            item.date.startsWith(month) &&
            compareDates(item.date, today) > 0 &&
            item.status === 'scheduled' &&
            item.kind !== 'buffer' &&
            !document.unavailableDays?.[item.date] &&
            item.suggestionStatus !== 'dismissed',
        )
        .map((item) => item.date),
    ),
  ];
  if (!candidates.length) return null;
  const load = (date: string) =>
    document.items
      .filter((item) => item.date === date && item.status !== 'skipped')
      .reduce((total, item) => total + item.durationMinutes, 0);
  return (
    candidates.sort((left, right) => load(left) - load(right) || compareDates(left, right))[0] ??
    null
  );
}

function lightestAvailableDate(
  document: TrackerDocument,
  afterDate: string,
  blocked: Set<string>,
): string {
  const month = afterDate.slice(0, 7);
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const candidates: string[] = [];
  for (let day = Number(afterDate.slice(8, 10)) + 1; day <= lastDay; day += 1) {
    const candidate = `${month}-${String(day).padStart(2, '0')}`;
    if (!blocked.has(candidate)) candidates.push(candidate);
  }
  if (!candidates.length) return nextAvailableUnblockedDate(afterDate, blocked);
  const load = (date: string) =>
    document.items
      .filter(
        (item) =>
          item.date === date && item.status !== 'skipped' && item.suggestionStatus !== 'dismissed',
      )
      .reduce((total, item) => total + item.durationMinutes, 0);
  return candidates.sort(
    (left, right) => load(left) - load(right) || compareDates(left, right),
  )[0]!;
}

function nextAvailableUnblockedDate(afterDate: string, blocked: Set<string>): string {
  let candidate = addDays(afterDate, 1);
  for (let attempts = 0; attempts < 370 && blocked.has(candidate); attempts += 1) {
    candidate = addDays(candidate, 1);
  }
  return candidate;
}
